// Battle Pass saisonnier — COSMÉTIQUE UNIQUEMENT (règle d'airain n°5):
// titres et cadres de cartes, jamais de puissance. Piste gratuite complète;
// piste premium visible mais verrouillée jusqu'au lancement (pas de paiement
// avant qu'il y ait un vrai produit — honnêteté d'abord).

export const PASS_SEASON = 'S1'
export const PASS_MAX_LEVEL = 20
export const PASS_POINTS_PER_LEVEL = 40
export const PASS_WIN = 10
export const PASS_LOSS = 4
export const PASS_QUEST = 15

export interface PassReward {
  kind: 'title' | 'frame'
  id: string
  label: string
  emoji: string
}

export interface PassTier {
  level: number
  free?: PassReward
  premium?: PassReward
}

export const PASS_TRACK: PassTier[] = [
  { level: 1, free: { kind: 'title', id: 't-dresseur', label: 'Dresseur', emoji: '🎮' } },
  { level: 2, free: { kind: 'frame', id: 'f-neon', label: 'Cadre Néon', emoji: '🔷' }, premium: { kind: 'title', id: 't-fondateur', label: 'Fondateur', emoji: '🏛️' } },
  { level: 3, free: { kind: 'title', id: 't-grinder', label: 'Grinder du Testnet', emoji: '⛏️' } },
  { level: 4, premium: { kind: 'frame', id: 'f-holo', label: 'Cadre Holo', emoji: '🪩' } },
  { level: 5, free: { kind: 'title', id: 't-cycle', label: 'Dompteur du Cycle', emoji: '🔁' } },
  { level: 6, free: { kind: 'frame', id: 'f-gold', label: 'Cadre Or', emoji: '🟡' } },
  { level: 8, free: { kind: 'title', id: 't-mineur', label: "Mineur d'Essence", emoji: '⚗️' } },
  { level: 10, free: { kind: 'frame', id: 'f-aurora', label: 'Cadre Aurora', emoji: '🌈' }, premium: { kind: 'title', id: 't-baleine', label: 'Future Baleine', emoji: '🐋' } },
  { level: 12, free: { kind: 'title', id: 't-arene', label: "Vétéran de l'Arène", emoji: '📡' } },
  { level: 14, premium: { kind: 'frame', id: 'f-void', label: 'Cadre du Vide', emoji: '🕳️' } },
  { level: 16, free: { kind: 'frame', id: 'f-ember', label: 'Cadre Braise', emoji: '🔥' } },
  { level: 18, free: { kind: 'title', id: 't-genesis', label: 'Fléau du Genesis', emoji: '💀' } },
  { level: 20, free: { kind: 'title', id: 't-legende', label: 'Légende Locale', emoji: '👑' }, premium: { kind: 'frame', id: 'f-genesis', label: 'Cadre Genesis', emoji: '🫀' } },
]

const KEY_PASS = 'cryptoket.pass'

interface PassState {
  season: string
  points: number
  title: string | null
  frame: string | null
}

function loadState(): PassState {
  try {
    const s = JSON.parse(localStorage.getItem(KEY_PASS) ?? '{}') as Partial<PassState>
    if (s.season === PASS_SEASON) {
      return { season: PASS_SEASON, points: s.points ?? 0, title: s.title ?? null, frame: s.frame ?? null }
    }
  } catch {
    // état corrompu → saison vierge
  }
  return { season: PASS_SEASON, points: 0, title: null, frame: null }
}

function saveState(s: PassState): void {
  localStorage.setItem(KEY_PASS, JSON.stringify(s))
}

export function passLevel(points: number): number {
  return Math.min(PASS_MAX_LEVEL, 1 + Math.floor(points / PASS_POINTS_PER_LEVEL))
}

export interface PassStatus {
  level: number
  points: number
  pct: number
  nextFree: PassReward | null
}

export function passStatus(): PassStatus {
  const s = loadState()
  const level = passLevel(s.points)
  const pct =
    level >= PASS_MAX_LEVEL ? 100 : Math.round(((s.points % PASS_POINTS_PER_LEVEL) / PASS_POINTS_PER_LEVEL) * 100)
  const nextFree = PASS_TRACK.find((t) => t.level > level && t.free)?.free ?? null
  return { level, points: s.points, pct, nextFree }
}

/** Ajoute des étoiles au pass. Renvoie le passage de niveau éventuel. */
export function addPassPoints(n: number): { level: number; leveledUp: boolean } {
  const s = loadState()
  const before = passLevel(s.points)
  s.points += n
  saveState(s)
  const level = passLevel(s.points)
  return { level, leveledUp: level > before }
}

/** Récompenses gratuites déverrouillées au niveau courant. */
export function unlockedRewards(): PassReward[] {
  const level = passLevel(loadState().points)
  return PASS_TRACK.filter((t) => t.level <= level && t.free).map((t) => t.free!)
}

export function equippedTitle(): PassReward | null {
  const s = loadState()
  return unlockedRewards().find((r) => r.id === s.title) ?? null
}

export function equippedFrame(): string | null {
  const s = loadState()
  return unlockedRewards().some((r) => r.id === s.frame) ? s.frame : null
}

export function equip(reward: PassReward | null, kind: 'title' | 'frame'): void {
  const s = loadState()
  if (kind === 'title') s.title = reward?.id ?? null
  else s.frame = reward?.id ?? null
  saveState(s)
}

/** Classes CSS des cadres de cartes (cosmétique pur). */
export const FRAME_CLASS: Record<string, string> = {
  'f-neon': 'pass-frame-neon',
  'f-gold': 'pass-frame-gold',
  'f-aurora': 'pass-frame-aurora',
  'f-ember': 'pass-frame-ember',
}
